// Comercial > Organizaciones: pure logic -- testable with node
// (organizacionesLogica.test.mjs). The list's filters and order, the tree's
// search and first expansion, the form (what to send, unsaved changes), and
// the split of an organization's contacts into direct and inherited ones.

export const sinTildes = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

// --- Filters (in the address, so they outlive opening and closing cards) ---

const FILTROS = ['q', 'tipo', 'pais', 'ciudad', 'estado', 'nivel']

// estado: 'no' (active, the default) | 'si' (archived) | 'todas' -- the backend's `archivadas`.
// ciudad: part of the city, accents and case aside; nivel: only the top-level
// organizations (no superior). Those two are applied here, not by the server.
export function filtrosDeParams(params) {
  const estado = params.get('estado')
  const pais = params.get('pais') || ''
  const tipo = Number(params.get('tipo'))
  return {
    q: params.get('q') || '',
    tipo: tipo > 0 ? tipo : null,
    pais: /^[A-Za-z]{2}$/.test(pais) ? pais.toUpperCase() : '',
    ciudad: params.get('ciudad') || '',
    estado: estado === 'si' || estado === 'todas' ? estado : 'no',
    nivel: params.get('nivel') === 'superior',
  }
}

// The address params with these filters, keeping the rest (the open card).
export function paramsConFiltros(params, filtros) {
  const next = new URLSearchParams(params)
  FILTROS.forEach(k => next.delete(k))
  if (filtros.q?.trim()) next.set('q', filtros.q.trim())
  if (filtros.tipo) next.set('tipo', String(filtros.tipo))
  if (filtros.pais) next.set('pais', filtros.pais)
  if (filtros.ciudad?.trim()) next.set('ciudad', filtros.ciudad.trim())
  if (filtros.estado && filtros.estado !== 'no') next.set('estado', filtros.estado)
  if (filtros.nivel) next.set('nivel', 'superior')
  return next
}

export const hayFiltros = (f) => !!(f.q.trim() || f.tipo || f.pais || f.ciudad?.trim() || f.estado !== 'no' || f.nivel)

// How many of the filters behind the "Filtros" button are on, for its counter.
// The tree only takes the state (country, city and top level don't apply to it).
export function contarFiltrosAdicionales(f, vista = 'lista') {
  if (vista === 'arbol') return f.estado !== 'no' ? 1 : 0
  return [f.pais, f.ciudad?.trim(), f.estado !== 'no', f.nivel].filter(Boolean).length
}

// What the page filters itself, after the server: the city and the top level.
export function filtrarEnPagina(lista, f) {
  const ciudad = sinTildes(f.ciudad).trim()
  return (f.nivel ? soloNivelSuperior(lista) : lista || [])
    .filter(o => !ciudad || sinTildes(o.ciudad).includes(ciudad))
}

// The query for GET /organizaciones (what the server filters).
export const consultaListado = (f) => ({
  q: f.q.trim() || undefined, tipo: f.tipo || undefined, pais: f.pais || undefined, archivadas: f.estado,
})

// --- The list ---

// Its superior's route: "CODELCO › División El Teniente" for El Teniente's
// Mina Sur ('' at the top). The server's `ruta` ends with the organization itself.
export function rutaSuperior(o) {
  if (!o?.superior) return ''
  const fin = ` › ${o.nombre_comercial}`
  return o.ruta?.endsWith(fin) ? o.ruta.slice(0, -fin.length) : o.superior.nombre_comercial
}

export const soloNivelSuperior = (lista) => (lista || []).filter(o => o.superior_id == null)

const VALOR = {
  // By its whole route, so a parent and its divisions sort together ("CODELCO",
  // "CODELCO › División El Teniente"), as the list shows them.
  nombre: (o) => o.ruta || o.nombre_comercial,
  tipos: (o) => (o.tipos || []).map(x => x.nombre).join(', '),
  superior: (o) => rutaSuperior(o),
  pais: (o, nombrePais) => (o.pais ? nombrePais(o.pais) : ''),
  ciudad: (o) => o.ciudad,
  sitio_web: (o) => o.sitio_web,
  identificador: (o) => o.identificador_tributario,
  direccion: (o) => o.direccion,
}
export const COLUMNAS_ORDEN = Object.keys(VALOR)

// --- Which columns the table shows (kept in the browser) ---
// Organización always shows; these are the user's choice, in this order on screen.
export const COLUMNAS_OPCIONALES = COLUMNAS_ORDEN.filter(c => c !== 'nombre')
export const COLUMNAS_PREDETERMINADAS = ['tipos']

// A saved choice, cleaned: known columns only, in screen order. Anything that
// isn't a list (nothing saved, junk) gives the default; an empty list is a choice.
export function columnasValidas(valor) {
  if (!Array.isArray(valor)) return [...COLUMNAS_PREDETERMINADAS]
  return COLUMNAS_OPCIONALES.filter(c => valor.includes(c))
}

export const alternarColumna = (columnas, c) =>
  columnasValidas(columnas.includes(c) ? columnas.filter(x => x !== c) : [...columnas, c])

// The order in force: sorting by a column that was hidden goes back to the name.
export const ordenVisible = (orden, columnas) =>
  (orden.campo === 'nombre' || columnas.includes(orden.campo) ? orden : { campo: 'nombre', dir: 'asc' })

const vacio = (v) => v === null || v === undefined || v === ''
const comparar = (a, b) => (typeof a === 'number' && typeof b === 'number'
  ? a - b
  : sinTildes(a).localeCompare(sinTildes(b), undefined, { numeric: true }))

// The list sorted by a column, either way; empty values always last; ties by name, then id.
//   nombrePais: code -> name in the user's language (countries sort by name)
export function ordenarOrganizaciones(lista, { campo = 'nombre', dir = 'asc' } = {}, nombrePais = (c) => c) {
  const valor = VALOR[campo] || VALOR.nombre
  const signo = dir === 'desc' ? -1 : 1
  return [...(lista || [])].sort((a, b) => {
    const va = valor(a, nombrePais)
    const vb = valor(b, nombrePais)
    if (vacio(va) !== vacio(vb)) return vacio(va) ? 1 : -1
    const c = vacio(va) ? 0 : comparar(va, vb) * signo
    return c || comparar(a.nombre_comercial, b.nombre_comercial) || a.id - b.id
  })
}

// Clicking a header: that column ascending, or the other way if it already was.
export const siguienteOrden = (orden, campo) =>
  (orden.campo === campo ? { campo, dir: orden.dir === 'asc' ? 'desc' : 'asc' } : { campo, dir: 'asc' })

// --- The tree (GET /organizaciones/arbol: [{ id, nombre_comercial, archivada, hijos }]) ---

export function contarNodos(nodos) {
  return (nodos || []).reduce((n, x) => n + 1 + contarNodos(x.hijos), 0)
}

export function idsConHijos(nodos, out = new Set()) {
  for (const n of nodos || []) {
    if (n.hijos?.length) { out.add(n.id); idsConHijos(n.hijos, out) }
  }
  return out
}

// Which branches start open without a search: level by level, each node whose
// children still fit within `limite` visible rows -- a small tree opens whole,
// a large one only its first levels (or none of a huge branch).
export function expansionInicial(nodos, limite = 40) {
  const abiertos = new Set()
  let visibles = (nodos || []).length
  let nivel = nodos || []
  while (nivel.length) {
    const siguiente = []
    for (const n of nivel) {
      const hijos = n.hijos || []
      if (!hijos.length || visibles + hijos.length > limite) continue
      abiertos.add(n.id)
      visibles += hijos.length
      siguiente.push(...hijos)
    }
    nivel = siguiente
  }
  return abiertos
}

// A search over the tree (name, without accents or case): the matches and the
// branches that lead to them, open; a match keeps its own children (closed)
// unless some of them match too. null when there is no search.
//   -> { nodos, abiertos: Set, coincidencias: Set }
export function filtrarArbol(nodos, q) {
  const n = sinTildes(q).trim()
  if (!n) return null
  const abiertos = new Set()
  const coincidencias = new Set()
  const visitar = (nodo) => {
    const hijos = (nodo.hijos || []).map(visitar).filter(Boolean)
    const coincide = sinTildes(nodo.nombre_comercial).includes(n)
    if (coincide) coincidencias.add(nodo.id)
    if (hijos.length) {
      abiertos.add(nodo.id)
      return { ...nodo, hijos }
    }
    return coincide ? nodo : null
  }
  return { nodos: (nodos || []).map(visitar).filter(Boolean), abiertos, coincidencias }
}

// The branches to open so `id` shows: its ancestors in the tree (and itself, to show its subordinates).
export function caminoA(nodos, id) {
  const buscar = (lista, camino) => {
    for (const n of lista || []) {
      if (n.id === id) return [...camino, n.id]
      const r = buscar(n.hijos, [...camino, n.id])
      if (r) return r
    }
    return null
  }
  return new Set(buscar(nodos, []) || [])
}

// --- The form ---

// The superiors an organization may take: active ones, never itself nor one of
// its descendants (the server answers 409 ciclo_jerarquia otherwise), by route.
//   lista: GET /organizaciones rows (id, superior_id, archivada, ruta); org: the one edited (null = new)
export function opcionesSuperior(lista, org) {
  const fuera = new Set()
  if (org) {
    const hijos = {}
    for (const o of lista || []) (hijos[o.superior_id] ||= []).push(o.id)
    const pendientes = [org.id]
    while (pendientes.length) {
      const i = pendientes.pop()
      if (fuera.has(i)) continue
      fuera.add(i)
      pendientes.push(...(hijos[i] || []))
    }
  }
  return (lista || []).filter(o => !o.archivada && !fuera.has(o.id))
    .sort((a, b) => sinTildes(a.ruta).localeCompare(sinTildes(b.ruta)) || a.id - b.id)
}

const CAMPOS = ['nombre_comercial', 'razon_social', 'identificador_tributario', 'superior_id', 'pais',
  'ciudad', 'direccion', 'sitio_web', 'observaciones']

// The form's values for an organization (a card from GET /organizaciones/{id}),
// or for a new one, maybe under a given superior.
export function formularioDe(org, superiorId = null) {
  const f = {}
  for (const c of CAMPOS) f[c] = org ? (org[c] ?? '') : ''
  f.superior_id = org ? (org.superior_id ?? null) : (superiorId ?? null)
  f.tipo_ids = org ? (org.tipos || []).map(x => x.id) : []
  return f
}

const limpio = (v) => (typeof v === 'string' ? (v.trim() || null) : (v ?? null))
const mismosIds = (a, b) => [...(a || [])].sort().join(',') === [...(b || [])].sort().join(',')

// What to send: every field for a new one (inicial = null); only what changed
// when editing, so an untouched name never triggers the duplicates check again.
export function datosOrganizacion(form, inicial) {
  const out = {}
  for (const c of CAMPOS) {
    const v = limpio(form[c])
    if (!inicial || v !== limpio(inicial[c])) out[c] = v
  }
  if (out.pais) out.pais = out.pais.toUpperCase()
  if (!inicial || !mismosIds(form.tipo_ids, inicial.tipo_ids)) out.tipo_ids = [...(form.tipo_ids || [])]
  return out
}

export const hayCambios = (form, inicial) => Object.keys(datosOrganizacion(form, inicial)).length > 0

// A website as a link: https:// added when it has no scheme. null for none.
export function urlSitio(s) {
  const v = String(s ?? '').trim()
  if (!v) return null
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(v) ? v : `https://${v}`
}

// A timestamp from the server: naive UTC (datetime.utcnow), read as UTC.
export function comoUtc(iso) {
  if (!iso) return null
  return /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`
}

// --- Contacts (GET /organizaciones/{id}/contactos: one row per contact) ---

// Direct contacts: some link with this very organization (they may also have
// links with subordinates). The rest come only from subordinates.
export function repartirContactos(filas) {
  const directos = []
  const deSubordinadas = []
  for (const f of filas || []) {
    (f.vinculos.some(v => !v.heredado) ? directos : deSubordinadas).push(f)
  }
  return { directos, deSubordinadas }
}
